import React, { useState, useRef, useEffect, useCallback } from 'react';
import { ChevronDown, Search, X } from 'lucide-react';

export interface SelectOption {
  id: string | number;
  name: string;
  code?: string;
  [key: string]: any;
}

interface SearchableSelectProps {
  options: SelectOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  loading?: boolean;
  className?: string;
  /** When 'id', value is the option id and we display the option name. When 'name' (default), value is the option name. */
  valueKey?: 'id' | 'name';
}

const SearchableSelect: React.FC<SearchableSelectProps> = ({
  options,
  value,
  onChange,
  placeholder = 'Select...',
  disabled = false,
  loading = false,
  className = '',
  valueKey = 'name',
}) => {
  const [open, setOpen]       = useState(false);
  const [query, setQuery]     = useState('');
  const containerRef          = useRef<HTMLDivElement>(null);
  const searchRef             = useRef<HTMLInputElement>(null);

  const byId = valueKey === 'id';
  const selectedLabel = byId
    ? (options.find(o => String(o.id) === String(value))?.name ?? value)
    : (options.find(o => o.name === value)?.name ?? value);

  const filtered = query.trim()
    ? options.filter(o =>
        o.name.toLowerCase().includes(query.toLowerCase())
      )
    : options;

  const handleOpen = () => {
    if (disabled || loading) return;
    setOpen(true);
    setQuery('');
    setTimeout(() => searchRef.current?.focus(), 50);
  };

  const handleSelect = useCallback((opt: SelectOption) => {
    onChange(byId ? String(opt.id) : opt.name);
    setOpen(false);
    setQuery('');
  }, [onChange, byId]);

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange('');
    setOpen(false);
    setQuery('');
  };

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { setOpen(false); setQuery(''); }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  return (
    <div ref={containerRef} style={{ position: 'relative', width: '100%' }} className={className}>
      {/* Trigger */}
      <div
        onClick={handleOpen}
        style={{
          display:        'flex',
          alignItems:     'center',
          justifyContent: 'space-between',
          width:          '100%',
          padding:        '0.5rem 0.75rem',
          border:         `1px solid ${open ? 'var(--color-primary)' : 'var(--color-gray-200)'}`,
          borderRadius:   '0.375rem',
          fontSize:       '1.1rem',
          background:     disabled || loading ? 'var(--color-gray-100)' : 'white',
          cursor:         disabled || loading ? 'not-allowed' : 'pointer',
          opacity:        disabled ? 0.5 : 1,
          color:          value ? 'var(--color-gray-800)' : 'var(--color-gray-400)',
          minHeight:      '3.2rem',
          boxSizing:      'border-box',
          transition:     'border-color 0.15s',
          outline:        'none',
          boxShadow:      open ? '0 0 0 2px var(--color-primary-100)' : 'none',
        }}
      >
        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {loading ? 'Loading...' : (value ? selectedLabel : placeholder)}
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: '2px', marginLeft: '6px', flexShrink: 0 }}>
          {value && !disabled && (
            <span
              onClick={handleClear}
              style={{
                display:    'flex',
                alignItems: 'center',
                padding:    '2px',
                borderRadius: '50%',
                color:      'var(--color-gray-400)',
              }}
              title="Clear"
            >
              <X size={13} />
            </span>
          )}
          <ChevronDown
            size={15}
            style={{
              color:     'var(--color-gray-400)',
              transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.15s',
            }}
          />
        </span>
      </div>

      {/* Dropdown */}
      {open && (
        <div
          style={{
            position:     'absolute',
            top:          'calc(100% + 4px)',
            left:         0,
            width:        '100%',
            zIndex:       9999,
            background:   'var(--color-primary-50)',
            border:       '1px solid var(--color-primary-light)',
            borderRadius: '0.375rem',
            boxShadow:    '0 4px 16px rgba(0,0,0,0.10)',
            overflow:     'hidden',
            boxSizing:    'border-box',
            transform:    'none',
            direction:    'ltr',
          }}
        >
          {/* Search box */}
          <div
            style={{
              display:    'flex',
              alignItems: 'center',
              gap:        '6px',
              padding:    '8px 12px 8px 22px',
              borderBottom: '1px solid var(--color-gray-100)',
              background: 'var(--color-gray-50)',
              boxSizing: 'border-box',
              direction: 'ltr',
            }}
          >
            <Search size={14} style={{ color: 'var(--color-gray-400)', flexShrink: 0 }} />
            <input
              ref={searchRef}
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Search..."
              style={{
                flex:       1,
                border:     'none',
                outline:    'none',
                background: 'transparent',
                fontSize:   '0.8125rem',
                color:      'var(--color-gray-800)',
              }}
            />
            {query && (
              <X
                size={13}
                style={{ color: 'var(--color-gray-400)', cursor: 'pointer', flexShrink: 0 }}
                onClick={() => setQuery('')}
              />
            )}
          </div>

          {/* Options list */}
          <div
            style={{
              maxHeight: '220px',
              overflowY: 'auto',
              overflowX: 'hidden',
              background: 'white',
              direction: 'ltr',
            }}
          >
            {filtered.length === 0 ? (
              <div
                style={{
                  padding:   '12px 14px',
                  fontSize:  '0.8125rem',
                  color:     'var(--color-gray-400)',
                  textAlign: 'center',
                }}
              >
                {query ? `No results for "${query}"` : 'No options available'}
              </div>
            ) : (
              filtered.map(opt => {
                const isSelected = byId ? String(opt.id) === String(value) : opt.name === value;
                return (
                  <div
                    key={opt.id}
                    onClick={() => handleSelect(opt)}
                    style={{
                      padding:    '8px 12px 8px 22px',
                      fontSize:   '0.875rem',
                      cursor:     'pointer',
                      background: isSelected ? 'var(--color-primary-50)' : 'white',
                      color:      isSelected ? 'var(--color-primary)' : 'var(--color-gray-800)',
                      fontWeight: isSelected ? 600 : 400,
                      borderLeft: isSelected ? '2px solid var(--color-primary)' : '2px solid transparent',
                      transition: 'background 0.1s, color 0.1s',
                      boxSizing: 'border-box',
                      whiteSpace: 'nowrap',
                      display: 'block',
                      width: '100%',
                      textIndent: 0,
                    }}
                    onMouseEnter={e => {
                      if (!isSelected) {
                        (e.currentTarget as HTMLDivElement).style.background = 'var(--color-primary-50)';
                        (e.currentTarget as HTMLDivElement).style.color = 'var(--color-primary)';
                      }
                    }}
                    onMouseLeave={e => {
                      if (!isSelected) {
                        (e.currentTarget as HTMLDivElement).style.background = 'white';
                        (e.currentTarget as HTMLDivElement).style.color = 'var(--color-gray-800)';
                      }
                    }}
                  >
                    <span style={{ display: 'inline-block', marginLeft: 0 }}>
                      {opt.name}
                    </span>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchableSelect;
